import React from 'react';
import {
  Button,
  CircularProgress,
  Tooltip,
  Box,
  Typography
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { ExportJob } from '../../api/invoiceApi';

interface ExportButtonProps {
  currentJob: ExportJob | null;
  isExporting: boolean;
  isLoading: boolean;
  error: string | null;
  onStartExport: () => void;
  onDownload: () => void;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'text' | 'outlined' | 'contained';
}

export const ExportButton: React.FC<ExportButtonProps> = ({
  currentJob,
  isExporting,
  isLoading,
  error,
  onStartExport,
  onDownload,
  disabled = false,
  size = 'medium',
  variant = 'outlined'
}) => {
  // Render completed state with download option
  if (currentJob?.status === 'COMPLETED' && currentJob.fileUrl) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant={variant}
          size={size}
          startIcon={<CheckCircleIcon color="success" />}
          endIcon={<FileDownloadIcon />}
          onClick={onDownload}
          sx={{
            borderColor: 'success.main',
            color: 'success.main',
            '&:hover': {
              borderColor: 'success.dark',
              bgcolor: 'success.light',
              color: 'success.dark'
            }
          }}
        >
          Download
        </Button>
        <Tooltip title="Start new export">
          <Button
            variant="text"
            size="small"
            onClick={onStartExport}
            sx={{ minWidth: 'auto', px: 1 }}
          >
            New
          </Button>
        </Tooltip>
      </Box>
    );
  }

  // Render error state
  if (currentJob?.status === 'FAILED' || error) {
    return (
      <Tooltip title={error || currentJob?.errorMessage || 'Export failed'}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant={variant}
            size={size}
            startIcon={<ErrorIcon color="error" />}
            onClick={onStartExport}
            disabled={disabled || isLoading}
            sx={{
              borderColor: 'error.main',
              color: 'error.main',
              '&:hover': {
                borderColor: 'error.dark',
                bgcolor: 'error.light',
                color: 'error.dark'
              }
            }}
          >
            Retry Export
          </Button>
        </Box>
      </Tooltip>
    );
  }

  // Render in-progress state
  if (isExporting) {
    const progress = currentJob?.progressPercent || 0;
    return (
      <Button
        variant={variant}
        size={size}
        disabled
        startIcon={
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              size={20}
              variant={progress > 0 ? 'determinate' : 'indeterminate'}
              value={progress}
            />
            {progress > 0 && (
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ fontSize: '0.6rem', lineHeight: 1 }}
                >
                  {progress}
                </Typography>
              </Box>
            )}
          </Box>
        }
      >
        {currentJob?.status === 'PENDING' ? 'Preparing...' : `Exporting${progress > 0 ? ` ${progress}%` : '...'}`}
      </Button>
    );
  }

  // Render default state
  return (
    <Button
      variant={variant}
      size={size}
      startIcon={isLoading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
      onClick={onStartExport}
      disabled={disabled || isLoading}
    >
      {isLoading ? 'Starting...' : 'Export'}
    </Button>
  );
};

export default ExportButton;
